import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Search, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';

const DatabasesPage = () => {
  const [databases, setDatabases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dbRes, orgRes] = await Promise.all([
        adminManagementApi.getDatabases(),
        adminManagementApi.getOrganizations()
      ]);
      setDatabases(dbRes.data);
      setOrganizations(orgRes.data || []);
    }
    catch (err) { console.error('Failed to load databases:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ID: selected.id, DatabaseName: selected.databaseName, DisplayName: selected.displayName, Description: selected.description || '', IsActive: selected.isActive, OrganizationId: selected.organizationId || null });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    setSelected(null);
    setEditing({ DatabaseName: '', DisplayName: '', Description: '', IsActive: true, OrganizationId: null });
    setDirtyFields(new Set(['DatabaseName', 'DisplayName']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const origMap = { DatabaseName: selected?.databaseName, DisplayName: selected?.displayName, Description: selected?.description || '', IsActive: selected?.isActive, OrganizationId: selected?.organizationId || null };
    const newDirty = new Set(dirtyFields);
    if (value !== origMap[field] || !selected) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    setSaving(true);
    try {
      if (editing.ID) { await adminManagementApi.updateDatabase(editing.ID, editing); }
      else { await adminManagementApi.createDatabase(editing); }
      const savedId = editing.ID;
      setEditing(null); setDirtyFields(new Set());
      const res = await adminManagementApi.getDatabases();
      setDatabases(res.data);
      if (savedId) { const refreshed = res.data.find(d => d.id === savedId); if (refreshed) setSelected(refreshed); }
      else setSelected(null);
    } catch (err) { console.error('Save failed:', err); }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    if (!editing?.ID) { cancelEdit(); return; }
    notify.confirm(`Delete database "${editing.DisplayName}"?`, async () => {
      setSaving(true);
      try { await adminManagementApi.deleteDatabase(editing.ID); setEditing(null); setSelected(null); setDirtyFields(new Set()); await load(); }
      catch (err) { console.error('Delete failed:', err); }
      finally { setSaving(false); }
    }, { onCancel: () => cancelEdit() });
  };

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0;

  const filtered = databases.filter(db =>
    !searchTerm ||
    db.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    db.databaseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    db.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="module-container">
      <div className="module-header">
        <div className="module-header-left">
          <Database size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Databases</h1>
          <span className="module-header-meta">{databases.length} instances</span>
        </div>
        <div className="module-header-actions">
          {isEditing ? (
            <button className="crud-btn" onClick={handleDelete} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={startAdd} title="Add database"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed"><button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search databases..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {filtered.length === 0 ? (
                <div className="task-list-empty">No databases found</div>
              ) : filtered.map(db => (
                <button key={db.id} onClick={() => { setSelected(db); setEditing(null); setDirtyFields(new Set()); }} className={`list-item-btn ${selected?.id === db.id ? 'selected' : ''}`}>
                  
                    <span className="list-item-primary">{db.displayName}</span>
                    <span className="list-item-secondary">{db.description || db.databaseName}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="detail-panel">
          {isEditing ? (
            <div className="detail-content">
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">{editing.ID ? 'Edit Database' : 'New Database'}</div></div>
                <div className="form-group col-span-8"><label>Display Name</label><input className={`form-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} autoFocus /></div>
                <div className="form-group col-span-8"><label>Database Name</label><input className={`form-input${dirtyFields.has('DatabaseName') ? ' dirty' : ''}`} value={editing.DatabaseName} onChange={e => handleFieldChange('DatabaseName', e.target.value)} placeholder="e.g. pmd-preprod-db-gwa01" /></div>
                <div className="form-group col-span-4"><label>Active</label>
                  <select className={`form-input${dirtyFields.has('IsActive') ? ' dirty' : ''}`} value={editing.IsActive ? 'true' : 'false'} onChange={e => handleFieldChange('IsActive', e.target.value === 'true')}>
                    <option value="true">Yes</option><option value="false">No</option>
                  </select>
                </div>
                <div className="form-group col-span-8"><label>Organization</label>
                  <select className={`form-input${dirtyFields.has('OrganizationId') ? ' dirty' : ''}`} value={editing.OrganizationId || ''} onChange={e => handleFieldChange('OrganizationId', e.target.value ? parseInt(e.target.value, 10) : null)}>
                    <option value="">— None —</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.displayName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-span-24"><label>Description</label><input className={`form-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} placeholder="What is this instance?" /></div>
              </div>
            </div>
          ) : selected ? (
            <div className="detail-content">
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">Database Details</div></div>
                <div className="form-group col-span-8"><label>Display Name</label><input className="form-input" value={selected.displayName} readOnly /></div>
                <div className="form-group col-span-8"><label>Database Name</label><input className="form-input" value={selected.databaseName} readOnly /></div>
                <div className="form-group col-span-4"><label>Active</label><input className="form-input" value={selected.isActive ? 'Yes' : 'No'} readOnly /></div>
                <div className="form-group col-span-8"><label>Organization</label><input className="form-input" value={selected.organizationName || '—'} readOnly /></div>
                {selected.description && <div className="form-group col-span-24"><label>Description</label><input className="form-input" value={selected.description} readOnly /></div>}
              </div>
            </div>
          ) : (
            <div className="dev-detail-empty"><Eye size={48} /><p>Select a database to view details</p><p className="hint">Or click + to register a new one</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabasesPage;
